// ===============================
// LogoutButton.jsx
// ===============================
// Bouton de déconnexion réutilisable
// ===============================

import React from "react";
import { useAuth } from "../contexts/AuthContext.jsx";
import { getContextualBackTarget, returnToContextualTarget } from "../contextualBack.js";
import { hasSupervisorResponsibleBackChain } from "../responsableBack.js";

export default function LogoutButton({ style = {}, closeWindowOnClick = false, label = "🚪 Déconnexion" }) {
  const { logout, isAuthenticated, user } = useAuth();
  const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const lockSecteurFromUrl = searchParams?.get("lockSecteur") === "1";
  const isScopedResponsableMode =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/responsable") &&
    (lockSecteurFromUrl || sessionStorage.getItem("scoped_responsable_digestif") === "1");
  const isSupervisorBridgeMode =
    Boolean(lockSecteurFromUrl && hasSupervisorResponsibleBackChain(searchParams?.get("back") || ""));

  if (!isAuthenticated) {
    return null; // Ne rien afficher si non connecté
  }

  const handleLogout = async () => {
    const roleNorm = String(user?.role || "").trim().toLowerCase();
    if (roleNorm === "supervision") {
      returnToContextualTarget("/superviseur/index.html");
      return;
    }

    if (isScopedResponsableMode) {
      if (isSupervisorBridgeMode) {
        returnToContextualTarget("/superviseur/index.html");
        return;
      }

      const target = getContextualBackTarget("/portail-medecin");
      try {
        const targetUrl = new URL(target, window.location.origin);
        const targetIsResponsibleBridgeStep =
          targetUrl.origin === window.location.origin &&
          /^\/(multisecteur\/)?responsable(\/|$)/.test(targetUrl.pathname) &&
          !/^\/(multisecteur\/)?responsable\/login\.html$/.test(targetUrl.pathname) &&
          (
            targetUrl.searchParams.has("back") ||
            targetUrl.searchParams.get("lockSecteur") === "1" ||
            targetUrl.searchParams.has("secteur")
          );

        if (targetIsResponsibleBridgeStep) {
          returnToContextualTarget("/portail-medecin");
          return;
        }
      } catch (_) {
        // Si le back est illisible, on garde le comportement historique ci-dessous.
      }

      // Mode secteur digestif verrouillé depuis portail médecin :
      // on restaure d'abord la session d'origine (médecin), puis retour portail médecin.
      try {
        await fetch("/api/v2/auth/support/read-only-restore", {
          method: "POST",
          credentials: "include",
        });
      } catch (_) {
        // Fallback silencieux: on tente quand même le retour visuel au portail.
      } finally {
        sessionStorage.removeItem("scoped_responsable_digestif");
        window.location.href = "/portail-medecin";
      }
      return;
    }

    if (closeWindowOnClick) {
      const isSupervisionUser = roleNorm === "supervision";
      let openerIsSupervision = false;
      try {
        const openerPath = String(window.opener?.location?.pathname || "");
        openerIsSupervision = openerPath.includes("/superviseur");
      } catch (_) {
        openerIsSupervision = false;
      }

      // Garde-fou: un compte non-superviseur ne doit jamais "retomber" sur la supervision.
      if (!isSupervisionUser && openerIsSupervision) {
        window.location.href = "/portail-medecin";
        return;
      }

      // Priorité: fermer l'onglet/page de travail sans déconnecter la session.
      try { window.close(); } catch (_) {}
      try { window.open("", "_self"); } catch (_) {}
      try { window.close(); } catch (_) {}
      // Fallback si fermeture bloquée par le navigateur.
      if (!window.closed) {
        window.location.href = isSupervisionUser ? "/superviseur/index.html" : "/portail-medecin";
      }
      return;
    }
    await logout();
    window.location.reload();
  };

  const defaultStyle = {
    padding: "8px 16px",
    borderRadius: "8px",
    border: "1px solid #dc2626",
    background: "#ffffff",
    color: "#dc2626",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  return (
    <button
      type="button"
      onClick={handleLogout}
      style={{ ...defaultStyle, ...style }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = "#dc2626";
        e.currentTarget.style.color = "#ffffff";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = "#ffffff";
        e.currentTarget.style.color = "#dc2626";
      }}
      title={isScopedResponsableMode ? "Retour au portail médecin" : "Se déconnecter"}
    >
      {isScopedResponsableMode ? "⬅️ Retour" : label}
    </button>
  );
}
